import React, { useState, useEffect, useRef } from "react";
import { Label, ErrorMessage, LabelWithContainer } from "./FormStyles";
import styled from "styled-components";
import Fonts from "../../themes/Fonts";
import Colors from "../../themes/Colors";
import { fadeIn } from "../Common/CommonStyles";
import useEmpresa from "../../hooks/useEmpresa";
import CheckRender from "../Common/CheckRender";
import { validationSchemaRequired } from "../../utils/SchemaValidations";
import { isEmptyValue } from "../../utils/StringUtil";

const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
  display: block;
`;

const SearchableSelectInput = styled.input`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: ${(props) => props.font || Fonts.familyVerlag};
  width: 100%;
  height: ${(props) => props.height || "37px"};
  min-width: ${(props) => props.minWidth || ""};
  cursor: pointer;
  background-color: white;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: blue;
    box-shadow: 0 0 5px rgba(0, 0, 255, 0.3);
  }

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 844px) {
    font-size: ${({ responsiveFontSize }) => responsiveFontSize || "18px"};
    padding: ${({ responsivePadding }) => responsivePadding || "2px"};
    width: 100%;
    height: 45px;
    animation: ${fadeIn} 0.5s ease-in;
  }

  @media (max-width: 320px) {
    width: 100%;
    animation: ${fadeIn} 0.5s ease-in;
  }
`;

const DropdownArrow = styled.div`
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  pointer-events: none;
  width: 0;
  height: 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #666;
  transition: transform 0.2s;
  ${(props) => props.isOpen && "transform: translateY(-50%) rotate(180deg);"}
`;

const DropdownList = styled.ul`
  position: absolute;
  top: calc(100% + 1px);
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
`;

const DropdownItem = styled.li`
  padding: 8px 10px;
  cursor: pointer;
  font-family: ${(props) => props.font || Fonts.familyVerlag};
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${(props) => props.hoverColor || Colors.primaryPrimera};
    color: white;
  }

  ${(props) =>
    props.selected &&
    `
    background-color: ${props.hoverColor || Colors.primaryPrimera};
    color: white;
  `}
`;

const CustomSearchableSelect = ({
  options,
  label,
  value,
  onChange,
  width,
  height,
  minWidth,
  disabled,
  required,
  showRequired,
  labelColor,
  labelWeight,
  isLabelWithContainer,
  labelWidth,
  labelHeight,
  placeholder = "Seleccionar...",
}) => {
  const empresa = useEmpresa();
  const [errors, setErrors] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const containerRef = useRef(null);

  useEffect(() => {
    if (required && showRequired && isEmptyValue(value)) {
      requireField();
    }
  }, [showRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    // Encontrar el label del valor seleccionado
    if (value) {
      const selectedOption = options?.find((opt) => opt.value === value);
      if (selectedOption) {
        setSearchTerm(selectedOption.label);
      }
    } else {
      setSearchTerm("");
    }
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        // Restaurar el texto al valor seleccionado si no hay selección
        if (value) {
          const selectedOption = options?.find((opt) => opt.value === value);
          if (selectedOption) {
            setSearchTerm(selectedOption.label);
          }
        } else {
          setSearchTerm("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value, options]);

  const validate = async (valueField) => {
    try {
      if (required) {
        await validationSchemaRequired.validate({
          requiredField: valueField,
        });
      }
      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const requireField = async () => {
    try {
      await validationSchemaRequired.validate({
        requiredField: null,
      });
      setErrors({});
    } catch (error) {
      setErrors({ [error.path]: error.message });
    }
  };

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setIsOpen(true);

    // Filtrar opciones basándose en el término de búsqueda
    const filtered = options?.filter((option) =>
      option.label.toLowerCase().includes(term.toLowerCase())
    );
    setFilteredOptions(filtered || []);
  };

  const handleOptionClick = (option) => {
    onChange(option.value);
    setSearchTerm(option.label);
    setIsOpen(false);
    validate(option.value);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm("");
    setFilteredOptions(options || []);
  };

  return (
    <>
      <CheckRender allowed={label}>
        <CheckRender allowed={isLabelWithContainer}>
          <div>
            <LabelWithContainer
              font={empresa?.LETRA_PRINCIPAL}
              labelWidth={labelWidth}
              labelHeight={labelHeight}
              labelColor={labelColor}
              labelWeight={labelWeight}
            >
              {label}
            </LabelWithContainer>
          </div>
        </CheckRender>
        <CheckRender allowed={!isLabelWithContainer}>
          <Label
            weight={labelWeight}
            color={labelColor}
            font={empresa?.LETRA_PRINCIPAL}
          >
            {label}
          </Label>
        </CheckRender>
      </CheckRender>

      <SearchableSelectContainer ref={containerRef}>
        <SearchableSelectInput
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          font={empresa?.LETRA_PRINCIPAL}
          height={height}
          minWidth={minWidth}
        />
        <DropdownArrow isOpen={isOpen} />

        {isOpen && filteredOptions.length > 0 && (
          <DropdownList>
            {filteredOptions.map((option) => (
              <DropdownItem
                key={option.value}
                onClick={() => handleOptionClick(option)}
                font={empresa?.LETRA_PRINCIPAL}
                hoverColor={empresa?.COLOR_PRINCIPAL}
                selected={option.value === value}
              >
                {option.label}
              </DropdownItem>
            ))}
          </DropdownList>
        )}

        {isOpen && filteredOptions.length === 0 && (
          <DropdownList>
            <DropdownItem font={empresa?.LETRA_PRINCIPAL}>
              No se encontraron resultados
            </DropdownItem>
          </DropdownList>
        )}
      </SearchableSelectContainer>

      {errors.requiredField && (
        <ErrorMessage>{errors.requiredField}</ErrorMessage>
      )}
    </>
  );
};

export default CustomSearchableSelect;

